import React from 'react';
import { StyleSheet } from 'react-native';

import { EntryDetails } from '@/api/entries/types/types';
import BetrLogo from '@/assets/icons/betr-logo-share';
import Picks from '@/assets/icons/picks-wordmark-logo';
import { LineSeparator } from '@/components/LineSeparator';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import {
    getEntryMultiplier,
    getEntryResultAmount,
    getEntryTitle,
    isEntryWin,
    isFreeEntry,
} from '@/feature/entries-pickem/entries-utils';
import { getPayoutLabel } from '@/hooks/use-entries-utils';
import { withOpacity } from '@/styles/styles';
import { Status } from '@/types/api.generated';
import { toLocaleCurrency } from '@/utils/numeric/currency';

export type PicksShareEntryHeaderProps = {
    entry?: EntryDetails;
};

export const PicksShareEntryHeader = ({ entry }: PicksShareEntryHeaderProps) => {
    const winner = isEntryWin(entry);
    const textColor = winner ? ds.colorToken.text.success : ds.colorToken.text.brand;
    const bgColor = winner
        ? withOpacity(ds.colorValue.background.success.strong, 0.1)
        : withOpacity(ds.colorValue.background.brand.emphasis, 0.1);

    const gameModeTitle = getEntryTitle(entry?.gameMode);
    // display "Cashed Out" only for share entry screen, the other label used in other screens for cashed out entries is "Payout"
    const entryPayoutLabel = entry?.status === Status.CashedOut ? 'Cashed Out' : getPayoutLabel(entry);
    return (
        <>
            <Box alignItems={'center'} paddingTop={'s20'}>
                <Row alignItems={'center'} gap={'s10'}>
                    <BetrLogo />
                    <Picks />
                </Row>
                <SizedBox value={12} />
                <Row justifyContent={'space-around'} gap={'s8'} alignItems={'center'} paddingBottom={'s12'}>
                    <Box alignItems={'center'} justifyContent={'center'} flex={1}>
                        <Text color={'neutral300'} variant={'bodySmall'}>
                            {gameModeTitle}
                        </Text>
                        <Text variant="headlineSmall" fontWeight={700}>
                            {toLocaleCurrency(entry?.amount ?? '')}
                        </Text>
                    </Box>
                    {!isFreeEntry(entry) ? (
                        <Box gap={'s4'} justifyContent={'center'} alignItems={'center'}>
                            <LineSeparator vertical style={styles.separator} />
                            <Box
                                borderRadius="r8"
                                paddingVertical="s4"
                                paddingHorizontal="s8"
                                alignItems="center"
                                style={{ backgroundColor: bgColor }}
                            >
                                <Text
                                    color={textColor}
                                    variant="labelMedium"
                                    textTransform="capitalize"
                                    fontWeight={600}
                                >
                                    {getEntryMultiplier(entry)}
                                </Text>
                            </Box>
                            <LineSeparator vertical style={styles.separator} />
                        </Box>
                    ) : null}
                    <Box alignItems={'center'} justifyContent={'center'} flex={1}>
                        <Text
                            color={isEntryWin(entry) ? ds.colorToken.text.success : 'neutral300'}
                            variant={'bodySmall'}
                        >
                            {entryPayoutLabel}
                        </Text>
                        <Text
                            variant={'headlineSmall'}
                            fontWeight={700}
                            color={isEntryWin(entry) ? ds.colorToken.text.success : ds.colorToken.text.primary}
                        >
                            {getEntryResultAmount(entry)}
                        </Text>
                    </Box>
                </Row>
            </Box>
        </>
    );
};

const styles = StyleSheet.create({
    separator: { opacity: 0.1, height: 8, marginTop: 2 },
});
